import { getCsrfToken } from "~/lib/utils"
// @ts-expect-error - @hotwired/turbo-rails doesn't have TypeScript declarations
import { Turbo } from "@hotwired/turbo-rails"
import { smtp_imap_reconnect_index_path, edit_smtp_imap_path, email_account_path } from "~/lib/routes"

type ReconnectAccount = {
  id: number
  provider: string
  connected_app_id?: number | null
}

type ReconnectOptions = {
  onSuccess?: () => void
}

/**
 * Reconnects an email account based on its provider type.
 *
 * For SMTP/IMAP accounts, posts to the reconnect endpoint.
 * For OAuth providers (gmail, outlook), redirects to the OAuth flow.
 *
 * @param account - The email account to reconnect (needs id, provider, connected_app_id)
 * @param options - Optional callbacks for success handling
 */
export const reconnectEmailAccount = async (
  account: ReconnectAccount,
  options: ReconnectOptions = {},
): Promise<void> => {
  if (account.provider === "smtp_imap") {
    try {
      const response = await fetch(smtp_imap_reconnect_index_path(account.id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        App?.displayFlash("success", i18n.t("js.email_accounts_center.actions.reconnect_success"))
        options.onSuccess?.()
      } else {
        // Reconnection failed, open the edit modal so user can fix settings
        if (account.connected_app_id) {
          Turbo.visit(edit_smtp_imap_path(account.connected_app_id, { tab: "servers" }), {
            frame: "edit_smtp_imap_connection",
          })
        }
      }
    } catch {
      if (App?.displayFlash) {
        App.displayFlash("error", i18n.t("js.email_accounts_center.actions.reconnect_error"))
      }
    }
  } else {
    // For OAuth providers (gmail, outlook), redirect to OAuth flow
    const redirectUrl = encodeURIComponent(window.location.pathname)
    window.location.href = `/auth/${account.provider}?redirect_url=${redirectUrl}`
  }
}

/**
 * Builds the reconnect URL for an email account.
 *
 * Useful for generating links that users can click to reconnect.
 *
 * @param account - The email account (needs id, provider)
 * @param redirectUrl - The URL to redirect to after OAuth (for OAuth providers)
 * @returns Object with url and method (POST for SMTP/IMAP, GET for OAuth)
 */
export const getReconnectUrl = (
  account: { id: number; provider: string },
  redirectUrl: string = window.location.pathname,
): { url: string; method: "POST" | "GET" } => {
  if (account.provider === "smtp_imap") {
    return {
      url: smtp_imap_reconnect_index_path(account.id),
      method: "POST",
    }
  }
  const encodedRedirect = encodeURIComponent(redirectUrl)
  return {
    url: `/auth/${account.provider}?redirect_url=${encodedRedirect}`,
    method: "GET",
  }
}

type DeleteAccountOptions = {
  confirmKey: string
  successKey: string
  errorKey: string
  confirmParams?: Record<string, unknown>
  successParams?: Record<string, unknown>
  onSuccess?: () => void
  handleTurboStream?: boolean
}

/**
 * Deletes/disconnects an email account after user confirmation.
 *
 * @param accountId - The ID of the email account to delete
 * @param options - Configuration for i18n keys, callbacks, and behavior
 * @returns Promise that resolves to true if deleted, false if cancelled or failed
 */
export const deleteEmailAccount = async (accountId: number, options: DeleteAccountOptions): Promise<boolean> => {
  const {
    confirmKey,
    successKey,
    errorKey,
    confirmParams = {},
    successParams = {},
    onSuccess,
    handleTurboStream = false,
  } = options

  const confirmed = window.confirm(i18n.t(confirmKey, confirmParams))
  if (!confirmed) return false

  try {
    const acceptHeader = handleTurboStream ? "text/vnd.turbo-stream.html, application/json" : "application/json"

    const response = await fetch(email_account_path(accountId), {
      method: "DELETE",
      headers: {
        Accept: acceptHeader,
        "X-CSRF-Token": getCsrfToken(),
      },
      credentials: "same-origin",
    })

    if (response.ok) {
      // Handle Turbo stream response if applicable
      if (handleTurboStream) {
        const contentType = response.headers.get("content-type")
        if (contentType?.includes("text/vnd.turbo-stream.html")) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        }
      }

      if (App?.displayFlash) {
        App.displayFlash("success", i18n.t(successKey, successParams))
      }
      onSuccess?.()
      return true
    } else {
      const error = await response.json().catch(() => ({ error: null }))
      if (App?.displayFlash) {
        App.displayFlash("error", error.error || i18n.t(errorKey))
      }
      return false
    }
  } catch {
    if (App?.displayFlash) {
      App.displayFlash("error", i18n.t(errorKey))
    }
    return false
  }
}
